import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Stack,
  Button,
  ButtonGroup,
  IconButton,
  TextField,
  CircularProgress,
} from "@mui/material";
import {
  BackupTable as ExportIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { DateRangePicker } from "rsuite";
import EditModal from "../billing/EditModal";
import DeleteModal from "../billing/DeleteModal";
import { secureApi } from "../../config";
import { Context } from "../../context";

const Billing = ({ patient_id }) => {
  const [billings, setBillings] = useState([]);
  const [filteredBillings, setFilteredBillings] = useState([]);
  const [billing, setBilling] = useState({});
  const [searchStr, setSearchStr] = useState("");
  const [filterDates, setFilterDates] = useState();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0); // Used to force re-fetch
  const { setLoggedIn } = useContext(Context);
  const navigate = useNavigate();
  const theme = useTheme();

  // Log when component mounts
  useEffect(() => {
    console.log("Billing component mounted with patient_id:", patient_id);
  }, []);

  const handleRefresh = () => {
    setRefreshKey(oldKey => oldKey + 1);
  };

  const handleEditModalOpen = (billing) => {
    setBilling(billing);
    setEditModalOpen(true);
  };

  const handleDeleteModalOpen = (billing) => {
    setBilling(billing);
    setDeleteModalOpen(true);
  };

  const handleEditModalClose = (_, reason) => {
    if (reason !== "backdropClick") {
      setEditModalOpen(false);
    }
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
  };

  const handleDeleteBilling = (billingID) => {
    const newBillings = [...billings];
    const billingIndex = newBillings.findIndex((b) => b._id === billingID);
    if (billingIndex !== -1) {
      newBillings.splice(billingIndex, 1);
      setBillings(newBillings);
    }
  };

  const handleUpdateBilling = (billing) => {
    const newBillings = [...billings];
    const billingIndex = newBillings.findIndex((b) => b._id === billing._id);
    if (billingIndex !== -1) {
      newBillings[billingIndex] = billing;
      setBillings(newBillings);
      return;
    }
  };

  const exportExcel = () => {
    const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";
    
    // Prepare data for export
    const dataWithHeaders = billings.map((billing) => {
      const obj = {};
      Object.keys(billing).forEach(key => {
        obj[key] = billing[key];
      });
      return obj;
    });
    
    const ws = XLSX.utils.json_to_sheet(dataWithHeaders);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "data");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(data, `billing${fileExtension}`);
  };

  useEffect(() => {
    if (patient_id) {
      setIsLoading(true);
      setError(null);
      console.log("Fetching billing data for patient:", patient_id);
      
      secureApi(window.localStorage.getItem("alex-med-token"))
        .get(`/billings/${patient_id}`)
        .then((response) => {
          console.log("API Response received:", response.data);
          
          // Extract billing data from response
          const { billings } = response.data;
          
          if (billings && Array.isArray(billings)) {
            console.log(`Successfully loaded ${billings.length} billing records`);
            setBillings(billings);
            setFilteredBillings(billings);
          } else {
            console.error("Billing data is not an array or is missing", billings);
            setError("Billing data format is invalid");
            setBillings([]);
            setFilteredBillings([]);
          }
        })
        .catch((e) => {
          console.error("Error fetching billing data:", e);
          setError(e.message || "Failed to fetch billing data");
          
          if (e.response && e.response.status === 401) {
            toast.warn("Session has been expired. You need to login again!");
            setLoggedIn(false);
            navigate("/login");
          } else if (e.response) {
            toast.error(e.response.data.message || "Failed to fetch billing data");
          } else {
            toast.error("Failed to connect to the server");
          }
          
          setBillings([]);
          setFilteredBillings([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      console.log("No patient_id provided, clearing billing data");
      setBillings([]);
      setFilteredBillings([]);
      setIsLoading(false);
    }
  }, [patient_id, navigate, setLoggedIn, refreshKey]);

  useEffect(() => {
    if (searchStr || filterDates) {
      // Filter billings based on search string and dates
      const filtered = billings.filter((billing) => {
        // Check if billing matches search string
        const matchesSearch =
          !searchStr ||
          Object.entries(billing).some(([key, value]) => {
            // Only search in string fields, not objects or arrays
            return (
              value && 
              typeof value === "string" && 
              value.toLowerCase().includes(searchStr.toLowerCase().trim())
            );
          });

        // Check if billing matches date range
        let matchesDates = true;
        if (filterDates && filterDates[0] && filterDates[1]) {
          const startDate = new Date(filterDates[0]);
          const endDate = new Date(filterDates[1]);

          if (billing.date_of_service) {
            // Handle different date formats
            let serviceDate;
            if (billing.date_of_service.includes("/")) {
              const [month, day, year] = billing.date_of_service.split("/");
              serviceDate = new Date(year, month - 1, day); // month is 0-based in JS
            } else {
              serviceDate = new Date(billing.date_of_service);
            }
            
            matchesDates = serviceDate >= startDate && serviceDate <= endDate;
          } else {
            matchesDates = false;
          }
        }

        return matchesSearch && matchesDates;
      });

      setFilteredBillings(filtered);
    } else {
      setFilteredBillings(billings);
    }
  }, [searchStr, filterDates, billings]);



  return (
    <Box sx={{ margin: 1, position: "relative" }}>
      <div style={{ width: "100%" }}>
        <Stack
          direction="row"
          sx={{ float: "right", marginBottom: "10px" }}
          gap={1}
        >
          <Button
            variant="contained"
            color="secondary"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            style={{ width: "fit-content" }}
            startIcon={<ExportIcon />}
            onClick={exportExcel}
            disabled={billings.length === 0}
          >
            Export Excel
          </Button>
          <DateRangePicker
            format="MM/dd/yyyy"
            character=" - "
            placeholder="Date of Service"
            size="lg"
            value={filterDates}
            onChange={(value) => setFilterDates(value)}
          />
          <TextField
            size="small"
            label="Search"
            variant="outlined"
            style={{ float: "right" }}
            value={searchStr}
            onChange={(e) => setSearchStr(e.target.value)}
          />
        </Stack>
      </div>
      
      {/* Clear the float */}
      <div style={{ clear: 'both' }}></div>
    

      
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '30px' }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#ffebee' }}>
          <Typography variant="h6" color="error">
            Error: {error}
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            sx={{ mt: 2 }}
            onClick={handleRefresh}
          >
            Retry
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto', maxHeight: '600px' }}>
          <Table size="small" aria-label="purchases" stickyHeader>
            <TableHead>
              <TableRow sx={{ background: "#fce5cd" }}>
                <TableCell>Insurance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Service Date</TableCell>
                <TableCell>Date billed</TableCell>
                <TableCell>CPT Code</TableCell>
                <TableCell>Diagnosis ICD Code</TableCell>
                <TableCell>Modifiers</TableCell>
                <TableCell>Charge</TableCell>
                <TableCell>Total Pay</TableCell>
                <TableCell>Adjusted Amount</TableCell>
                <TableCell>A/R</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBillings && filteredBillings.length > 0 ? (
                filteredBillings.map((billing, index) => (
                  <TableRow
                    sx={{
                      "& > *": {
                        borderBottom: "unset",
                        background: index % 2 === 0 ? theme.palette.action.hover : "white",
                      },
                    }}
                    key={billing._id || index}
                  >
                    <TableCell>{billing.insurance || ""}</TableCell>
                    <TableCell>{billing.status || ""}</TableCell>
                    <TableCell>{billing.date_of_service || ""}</TableCell>
                    <TableCell>{billing.date_billed || ""}</TableCell>
                    <TableCell>{billing.cpt_code || ""}</TableCell>
                    <TableCell>{billing.diagnosis_icd_code || ""}</TableCell>
                    <TableCell>{billing.modifiers || ""}</TableCell>
                    <TableCell>{billing.charge || ""}</TableCell>
                    <TableCell>{billing.total_pay || ""}</TableCell>
                    <TableCell>{billing.adjusted_amount || ""}</TableCell>
                    <TableCell>{billing.a_r || ""}</TableCell>
                    <TableCell>
                      <ButtonGroup size="small">
                        <IconButton
                          variant="contained"
                          color="warning"
                          onClick={() => handleEditModalOpen(billing)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          variant="contained"
                          color="error"
                          onClick={() => handleDeleteModalOpen(billing)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={12} sx={{ textAlign: "center", p: 3 }}>
                    <Typography variant="h6">No Billing Information</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <DeleteModal
        open={deleteModalOpen}
        billing={billing}
        handleClose={handleDeleteModalClose}
        handleDeleteBilling={handleDeleteBilling}
      />
      <EditModal
        open={editModalOpen}
        billing={billing}
        setBilling={setBilling}
        handleClose={handleEditModalClose}
        handleUpdateBilling={handleUpdateBilling}
      />
    </Box>
  );
};

export default Billing;